import { Button } from "@/components/ui/button";
import { X, RotateCcw, Download, ZoomIn, ZoomOut, Share2, Edit3, Trash2 } from "lucide-react";
import { useState, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useIsMobile } from "@/hooks/useMobile";
import { cn } from "@/lib/utils";

interface EnhancedPhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  photo: {
    id: string;
    title: string;
    image: string;
    description?: string;
    timestamp?: number;
  };
  onRetake?: () => void;
  onDownload?: () => void;
  onDelete?: () => void;
  onShare?: () => void;
  showEditTools?: boolean;
}

export function EnhancedPhotoModal({
  isOpen,
  onClose,
  photo,
  onRetake,
  onDownload,
  onDelete,
  onShare,
  showEditTools = false
}: EnhancedPhotoModalProps) {
  const isMobile = useIsMobile();
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [showFilters, setShowFilters] = useState(false);
  
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDownload = useCallback(() => {
    if (!onDownload) {
      // Default download behavior
      const link = document.createElement('a');
      link.href = photo.image;
      link.download = `${photo.title.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      onDownload();
    }
  }, [photo, onDownload]);

  const handleShare = useCallback(async () => {
    if (onShare) {
      onShare();
      return;
    }

    // Native Web Share API
    if (navigator.share) {
      try {
        // Convert data URL to blob
        const response = await fetch(photo.image);
        const blob = await response.blob();
        const file = new File([blob], `${photo.title}.jpg`, { type: 'image/jpeg' });
        
        await navigator.share({
          title: photo.title,
          text: `Check out this dental photo: ${photo.title}`,
          files: [file]
        });
      } catch (error) {
        console.error('Share failed:', error);
        // Fallback to copying image URL
        navigator.clipboard.writeText(photo.image);
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(photo.image);
        alert('Photo URL copied to clipboard!');
      } catch (error) {
        console.error('Copy failed:', error);
      }
    }
  }, [photo, onShare]);

  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev * 1.5, 5));
  }, []);

  const handleZoomOut = useCallback(() => {
    const newZoom = Math.max(zoomLevel / 1.5, 1);
    setZoomLevel(newZoom);
    if (newZoom === 1) {
      setPanPosition({ x: 0, y: 0 });
    }
  }, [zoomLevel]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
    }
  }, [zoomLevel, panPosition]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 1) {
      setPanPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart, zoomLevel]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const resetFilters = useCallback(() => {
    setBrightness(100);
    setContrast(100);
    setSaturation(100);
  }, []);

  const formatTimestamp = useCallback((timestamp?: number) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleString();
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
    setBrightness(100);
    setContrast(100);
    setSaturation(100);
    setShowFilters(false);
    setIsDragging(false);
  }, [isOpen, photo.id]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  /** Fits preview to viewport minus header + action bar (approx.) */
  const previewMax = "min(68vh, calc(100dvh - 13.5rem))";

  const modal = (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90 p-2 sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={cn(
          "flex min-h-0 w-full max-h-[95vh] flex-col overflow-hidden rounded-xl bg-white shadow-2xl",
          isMobile ? "max-w-sm" : "max-w-6xl",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b bg-white p-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold truncate">{photo.title}</h3>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              {photo.description && (
                <span className="truncate">{photo.description}</span>
              )}
              {photo.timestamp && (
                <span className="text-xs">{formatTimestamp(photo.timestamp)}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {showEditTools && (
              <Button 
                onClick={() => setShowFilters(!showFilters)} 
                variant="ghost" 
                size="sm"
                className={cn(showFilters && "bg-gray-100")}
              >
                <Edit3 className="w-4 h-4" />
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Filter Controls */}
        {showFilters && (
          <div className="shrink-0 border-b bg-gray-50 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Brightness</label>
                <input
                  type="range"
                  min="50"
                  max="150"
                  value={brightness}
                  onChange={(e) => setBrightness(Number(e.target.value))}
                  className="w-full"
                />
                <span className="text-xs text-gray-500">{brightness}%</span>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Contrast</label>
                <input
                  type="range"
                  min="50"
                  max="150"
                  value={contrast}
                  onChange={(e) => setContrast(Number(e.target.value))}
                  className="w-full"
                />
                <span className="text-xs text-gray-500">{contrast}%</span>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Saturation</label>
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={saturation}
                  onChange={(e) => setSaturation(Number(e.target.value))}
                  className="w-full"
                />
                <span className="text-xs text-gray-500">{saturation}%</span>
              </div>
            </div>
            <div className="flex justify-end mt-2">
              <Button onClick={resetFilters} variant="outline" size="sm">
                Reset Filters
              </Button>
            </div>
          </div>
        )}

        {/* Image Container — min-h-0 + flex-1 so the preview gets real height (was huge empty margins) */}
        <div
          ref={containerRef}
          className={cn(
            "relative flex min-h-0 flex-1 cursor-grab flex-col overflow-hidden bg-neutral-950 active:cursor-grabbing",
          )}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div className="flex min-h-[min(42vh,22rem)] flex-1 items-center justify-center px-2 py-3 sm:px-4">
            <div
              className="flex max-h-full w-full max-w-full items-center justify-center"
              style={{
                transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                transition: isDragging ? "none" : "transform 0.3s ease-out",
              }}
            >
              <img
                ref={imageRef}
                src={photo.image}
                alt={photo.title}
                className="h-auto w-full max-w-full rounded-md object-contain shadow-lg select-none [image-orientation:from-image]"
                style={{
                  maxHeight: previewMax,
                  filter: `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`,
                }}
                draggable={false}
              />
            </div>
          </div>

          {/* Zoom Controls */}
          <div className="pointer-events-auto absolute top-3 right-3 z-20 flex flex-col gap-1.5">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleZoomIn();
              }}
              className="h-9 w-9 bg-white/95 p-0 shadow-md hover:bg-white"
              disabled={zoomLevel >= 5}
              aria-label="Zoom in"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleZoomOut();
              }}
              className="h-9 w-9 bg-white/95 p-0 shadow-md hover:bg-white"
              disabled={zoomLevel <= 1}
              aria-label="Zoom out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            {zoomLevel > 1 && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  resetZoom();
                }}
                className="bg-white/95 px-2 text-xs shadow-md hover:bg-white"
              >
                Reset
              </Button>
            )}
          </div>

          {zoomLevel > 1 && (
            <div className="pointer-events-none absolute bottom-3 right-3 z-20 rounded bg-black/75 px-2 py-1 text-sm text-white">
              {Math.round(zoomLevel * 100)}%
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="shrink-0 border-t bg-white p-4">
          <div className={cn(
            "flex gap-2",
            isMobile ? "flex-col" : "flex-row flex-wrap"
          )}>
            {onRetake && (
              <Button 
                onClick={onRetake}
                variant="outline"
                className={cn(
                  "flex items-center justify-center gap-2",
                  isMobile ? "w-full" : "flex-1"
                )}
              >
                <RotateCcw className="w-4 h-4" />
                Retake Photo
              </Button>
            )}
            
            <Button 
              onClick={handleDownload}
              variant="outline"
              className={cn(
                "flex items-center justify-center gap-2",
                isMobile ? "w-full" : "flex-1"
              )}
            >
              <Download className="w-4 h-4" />
              Download
            </Button>

            <Button 
              onClick={handleShare}
              variant="outline"
              className={cn(
                "flex items-center justify-center gap-2",
                isMobile ? "w-full" : "flex-1"
              )}
            >
              <Share2 className="w-4 h-4" />
              Share
            </Button>

            {onDelete && (
              <Button 
                onClick={onDelete}
                variant="outline"
                className={cn(
                  "flex items-center justify-center gap-2 text-red-600 border-red-200 hover:bg-red-50",
                  isMobile ? "w-full" : "flex-1"
                )}
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </Button>
            )}
            
            <Button 
              onClick={onClose}
              className={cn(
                "flex items-center justify-center gap-2",
                isMobile ? "w-full" : "flex-1"
              )}
            >
              Keep Photo
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(modal, document.body) : null;
}